/**
 * Payments block copy (COL-533). Same dictionary shape as the other V2 copy
 * files so the six-language release picks it up.
 */

export function getTenantPaymentsLabels(lang: "en" | "de") {
  return lang === "de"
    ? {
        nextPayment: "Nächste Zahlung",
        dueOn: "Fällig am",
        nothingDue: "Keine offenen Forderungen hinterlegt.",
        overdue: "Überfällig",
        sepaInProgressStatus: "SEPA-Lastschrift läuft",
        securityDeposit: "Kaution",
        dunningFeesLabel: "Mahngebühren",
        upcoming: "Geplant",
        recent: "Zuletzt bezahlt",
        setUpSepa: "SEPA-Mandat einrichten",
        payNow: "Jetzt zahlen",
        payNowCollected: "Lastschrift ist unterwegs.",
        payNowAlreadyPaid: "Diese Miete ist bereits bezahlt.",
        payNowInFlight: "Für diesen Monat läuft bereits eine Lastschrift.",
        payNowFailed: "Die Zahlung konnte nicht gestartet werden.",
        changeMandate: "Mandat verwalten",
        paymentMethod: "Zahlungsmethode",
        paymentMethodSepa: "SEPA-Lastschrift{iban}, Einzug am {day}.",
        paymentMethodTransfer: "Überweisung, fällig am {day}.",
        paymentMethodCard: "Karte · fällig am {day}., du zahlst selbst.",
        paymentMethodNone: "Noch keine Zahlungsmethode hinterlegt.",
        // The design splits the method into a NAME and a detail line, with a
        // badge for a live mandate, instead of one sentence.
        railSepaName: "SEPA-Lastschrift",
        railTransferName: "Überweisung",
        railCardName: "Karte",
        railSepaDetail: "{iban}Abbuchung jeweils am {day}.",
        railTransferDetail: "Fällig jeweils am {day}.",
        // Load-bearing: a card payment is NOT collected automatically -- the
        // tenant has to press Pay now and finish checkout.
        railCardDetail:
          "Du zahlst jede Miete selbst über „Jetzt zahlen“. Es wird nichts automatisch abgebucht.",
        // Only used when the rail really is SEPA with a live mandate.
        nextPaymentCollected: "Abbuchung am {date} per SEPA",
        paymentMethodActive: "Aktiv",
        paymentMethodOpen: "Offen",
        paymentMethodChange: "Ändern",
        methodSepaTitle: "SEPA-Lastschrift",
        methodSepaBlurb: "Die Miete wird automatisch am {day}. eingezogen.",
        methodSepaRecommended: "Empfohlen",
        methodTransferTitle: "Überweisung",
        methodTransferBlurb:
          "Du überweist sie selbst bis zum {day}. des Monats.",
        methodCardTitle: "Karte",
        methodCardBlurb: "Kredit- oder Debitkarte, du zahlst je Monat selbst.",
        methodSepaDetail:
          "Mandat für {iban}. Die Miete wird am {day}. eingezogen, du musst nichts tun.",
        methodSepaDetailNone:
          "Noch kein Mandat hinterlegt — ohne Mandat wird nichts eingezogen. Richte es unten ein.",
        methodTransferDetail:
          "Die Bankverbindung steht bei deiner nächsten Zahlung. Tipp: Richte einen Dauerauftrag zum {day}. ein.",
        methodCardDetail:
          "Mit Karte zahlst du jede Miete selbst über „Jetzt zahlen“. Es wird nichts automatisch abgebucht.",
        methodSave: "Speichern",
        methodSaved: "Zahlungsmethode aktualisiert.",
        methodSaveFailed: "Die Zahlungsmethode konnte nicht geändert werden.",
        // Routed to Hilfe, not to the old dashboard: mandate management lives
        // on this screen now, and the request itself is a support matter.
        methodMandateLocked:
          "Ein aktives SEPA-Mandat kann nur mit unserer Zustimmung beendet werden. Melde dich über Hilfe, um das anzufragen.",
        // The Paid list is a disclosure, collapsed until clicked (Neel,
        // 13 Oct): a long history otherwise dominates the card. {n} is the
        // entry count, shown on the trigger so it reads without opening.
        paidToggle: "Bezahlt ({n})",
        paidShow: "Einblenden",
        paymentHistory: "Zahlungshistorie",
        directDebit: "Lastschrift",
        paidHide: "Ausblenden",
        // Shown where SEPA details would be, when the tenant has chosen bank
        // transfer or has set no method at all.
        bankTransferHeading: "Überweisung an coliviq",
        bankTransferHint:
          "Bitte gib deine Buchungsnummer als Verwendungszweck an.",
        bankAccountHolder: "Kontoinhaber",
        bankIban: "IBAN",
        bankBic: "BIC",
        // No IBAN configured for this environment. Never invent one.
        bankDetailsUnavailable:
          "Bankverbindung auf Anfrage — melde dich über Hilfe bei uns.",
        rentLabel: "Miete",
        deposit: "Kaution",
        loading: "Zahlungen werden geladen …",
        loadFailed:
          "Deine Zahlungen konnten nicht geladen werden. Lade die Seite neu.",
      }
    : {
        nextPayment: "Next payment",
        dueOn: "Due on",
        nothingDue: "No open charges on record.",
        overdue: "Overdue",
        sepaInProgressStatus: "SEPA in progress",
        securityDeposit: "Security deposit",
        dunningFeesLabel: "Late fees",
        upcoming: "Upcoming",
        recent: "Recently paid",
        setUpSepa: "Set up SEPA mandate",
        payNow: "Pay Now",
        payNowCollected: "The direct debit is on its way.",
        payNowAlreadyPaid: "This rent is already paid.",
        payNowInFlight: "A direct debit is already in progress for this month.",
        payNowFailed: "Could not start the payment.",
        changeMandate: "Manage mandate",
        paymentMethod: "Payment method",
        paymentMethodSepa: "SEPA direct debit{iban}, collected on the {day}",
        paymentMethodTransfer: "Bank transfer, due on the {day}",
        paymentMethodCard: "Card \u00b7 due on the {day}, paid by you",
        paymentMethodNone: "No payment method set up yet.",
        railSepaName: "SEPA direct debit",
        railTransferName: "Bank transfer",
        railCardName: "Card",
        railSepaDetail: "{iban}Collected on the {day} of each month.",
        railTransferDetail: "Due on the {day} of each month.",
        railCardDetail:
          "You pay each rent yourself via \u201cPay now\u201d. Nothing is debited automatically.",
        nextPaymentCollected: "Collected on {date} by SEPA",
        paymentMethodActive: "Active",
        paymentMethodOpen: "Open",
        paymentMethodChange: "Change",
        methodSepaTitle: "SEPA direct debit",
        methodSepaBlurb: "Rent is collected automatically on the {day}.",
        methodSepaRecommended: "Recommended",
        methodTransferTitle: "Bank transfer",
        methodTransferBlurb:
          "You transfer it yourself by the {day} of the month.",
        methodCardTitle: "Card",
        methodCardBlurb: "Credit or debit card, paid by you each month.",
        methodSepaDetail:
          "Mandate for {iban}. Rent is collected on the {day}, nothing for you to do.",
        methodSepaDetailNone:
          "No mandate yet \u2014 without one nothing is collected. Set it up below.",
        methodTransferDetail:
          "The bank details are shown with your next payment. Tip: set up a standing order for the {day}.",
        methodCardDetail:
          "With card you pay each rent yourself via \u201cPay now\u201d. Nothing is debited automatically.",
        methodSave: "Save",
        methodSaved: "Payment method updated.",
        methodSaveFailed: "Could not change the payment method.",
        methodMandateLocked:
          "An active SEPA mandate can only be ended with our approval. Message us from Help to request it.",
        paidToggle: "Paid ({n})",
        paidShow: "Show",
        paymentHistory: "Payment history",
        directDebit: "Direct debit",
        paidHide: "Hide",
        bankTransferHeading: "Bank transfer to coliviq",
        bankTransferHint: "Please use your booking number as the reference.",
        bankAccountHolder: "Account holder",
        bankIban: "IBAN",
        bankBic: "BIC",
        bankDetailsUnavailable:
          "Bank details on request — message us from Help.",
        rentLabel: "Rent",
        deposit: "Deposit",
        loading: "Loading payments …",
        loadFailed:
          "Your payments could not be loaded. Please reload the page.",
      };
}
